// Site-wide "Get My Quote" modal context.
//
// Replaces the old `#hero-form` anchor pattern which scrolled to the ZIP
// field without selecting a tab. Now: ANY CTA can call openModal(category?)
// — if no category is given, we infer it from the current route so a user
// on /insurance/florida lands on the Insurance tab, a user on /new-car-loans
// lands on the New Auto Loan sub-tab, etc.
//
// Also reads ?quote=<category> on mount so external/social links can deep
// link straight to a specific funnel (e.g. /best-auto-loan-lenders?quote=auto-loan-new).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { track, events } from "./analytics";

export const VALID_CATEGORIES = [
  "insurance",
  "auto-loan-new",
  "auto-loan-used",
  "refinance",
  "warranty",
];

// Heuristic: route path → most likely user intent.
// Order matters — earliest match wins, so put the most-specific patterns first.
const ROUTE_RULES = [
  { match: /^\/auto-insurance(\/|$)/, category: "insurance" },
  { match: /^\/insurance(\/|$)/, category: "insurance" },
  { match: /auto-insurance-cost-estimator|insurance-cost-estimator/, category: "insurance" },
  { match: /^\/auto-loan-refinance-calculator/, category: "refinance" },
  { match: /^\/auto-loan-calculator/, category: "auto-loan-new" },
  { match: /^\/car-affordability-calculator/, category: "auto-loan-new" },
  { match: /^\/new-car-loans/, category: "auto-loan-new" },
  { match: /^\/used-car-loans/, category: "auto-loan-used" },
  { match: /^\/first-time-buyer-auto-loans/, category: "auto-loan-new" },
  { match: /^\/auto-loans(\/|$)/, category: "auto-loan-new" },
  { match: /^\/best-auto-loan-lenders/, category: "auto-loan-new" },
  { match: /refinance/, category: "refinance" },
  { match: /^\/(?:articles|guides)\/auto-refinance/, category: "refinance" },
  { match: /^\/(?:articles|guides)\/.*(?:refinance|payment)/, category: "refinance" },
  { match: /extended-warranty/, category: "warranty" },
];

export function inferCategoryFromPath(pathname) {
  for (const rule of ROUTE_RULES) {
    if (rule.match.test(pathname)) return rule.category;
  }
  return "insurance"; // Historical default
}

const QuoteModalContext = createContext({
  open: () => {},
  close: () => {},
  isOpen: false,
  category: "insurance",
});

export function QuoteModalProvider({ children, ModalComponent }) {
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState("insurance");
  // Optional headline/sub override — lets a caller (e.g. the inline insurance
  // estimator) hand off a personalized message like "Drivers in Michigan
  // typically save $920 by comparing 3+ insurers". When null, the modal renders
  // its default copy. Cleared on every close so a subsequent open doesn't
  // accidentally re-use a stale personalized headline.
  const [headlineOverride, setHeadlineOverride] = useState(null);
  const [subOverride, setSubOverride] = useState(null);
  // Optional success-card context — when a caller passes estimator outputs
  // (state code, estimated annual premium, credit band, projected savings…)
  // the post-submit success card uses them to render a personalized rate
  // range projection instead of the generic "we'll be in touch" copy. Same
  // lifecycle as the headline override — cleared on close.
  const [successContext, setSuccessContext] = useState(null);
  const location = useLocation();

  const open = useCallback(
    (cat, options = {}) => {
      const resolved =
        cat && VALID_CATEGORIES.includes(cat)
          ? cat
          : inferCategoryFromPath(location.pathname);
      setCategory(resolved);
      setHeadlineOverride(options.headline || null);
      setSubOverride(options.sub || null);
      setSuccessContext(options.successContext || null);
      setIsOpen(true);
      // GA4: every modal-open is a top-of-funnel event. Source page + category
      // attribution makes the GA4 funnel report meaningful — we can see which
      // page (home / state page / lender page / article) drives the most
      // qualified modal opens.
      track(events.MODAL_OPEN, {
        modal_name: "quote_modal",
        category: resolved,
        page_location: location.pathname,
        // Surface the override flag in analytics — lets us A/B measure whether
        // personalized headlines actually lift modal→submit conversion vs. the
        // default copy. The headline string itself is intentionally NOT sent
        // (free-form text inflates GA4 event-param cardinality).
        personalized_headline: Boolean(options.headline),
        personalized_success: Boolean(options.successContext),
      });
    },
    [location.pathname],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setHeadlineOverride(null);
    setSubOverride(null);
    setSuccessContext(null);
    track(events.MODAL_CLOSE, { modal_name: "quote_modal", category });
  }, [category]);

  // ?quote=<category> deep-link: open the modal with that tab pre-selected.
  // Runs on every search-string change (incl. back/forward).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("quote");
    if (q && VALID_CATEGORIES.includes(q)) {
      setCategory(q);
      setIsOpen(true);
    }
  }, [location.search]);

  // Hash interceptor: any existing `<Link to="/#hero-form">` (25+ scattered
  // CTAs across the site) now triggers the modal with a route-inferred
  // category instead of just scrolling to a ZIP input. Strips the hash
  // afterward so a refresh doesn't re-pop the modal.
  useEffect(() => {
    if (location.hash !== "#hero-form") return;
    setCategory(inferCategoryFromPath(location.pathname));
    setIsOpen(true);
    // Replace state to remove the hash without adding a history entry.
    if (typeof window !== "undefined" && window.history) {
      window.history.replaceState(
        null,
        "",
        `${location.pathname}${location.search}`,
      );
    }
  }, [location.hash, location.pathname, location.search]);

  // NOTE: deliberately NOT closing on every pathname change — that would
  // stomp the hash/query effects above when a CTA navigates+opens in one
  // gesture. ESC, backdrop click, and submit handle the close paths.

  const value = useMemo(
    () => ({ open, close, isOpen, category, successContext }),
    [open, close, isOpen, category, successContext],
  );

  return (
    <QuoteModalContext.Provider value={value}>
      {children}
      {ModalComponent ? (
        <ModalComponent
          isOpen={isOpen}
          category={category}
          headlineOverride={headlineOverride}
          subOverride={subOverride}
          successContext={successContext}
          onClose={close}
        />
      ) : null}
    </QuoteModalContext.Provider>
  );
}

export function useQuoteModal() {
  return useContext(QuoteModalContext);
}
